import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import SharedViewAPI from "../../../api/api/SharedScreenAPI";
import { useAtomValue } from "jotai";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";

interface Props {
  viewID: string | undefined;
  recordID: string | undefined;
  onSuccess: () => void;
  onError: (err: string) => void;
  parentPageID?: string;
  parentViewID?: string;
  documentID?: string;
}

// Handles unsharing pages AND views AND documents.
export default function useUnshareScreen() {
  const client = useQueryClient();
  const workspaceID = useAtomValue(workspaceIdAtom);
  const tenantID = useAtomValue(tenantIDAtom);

  const unshareScreen = useMutation({
    mutationFn: ({
      viewID,
      recordID,
      parentPageID,
      parentViewID,
      documentID,
    }: Props) => {
      if (!workspaceID) return Promise.reject("Missing workspace ID!");

      return SharedViewAPI.unshareScreen(
        workspaceID,
        viewID,
        recordID,
        parentPageID,
        parentViewID,
        documentID
      ).then((data) => data.data.data);
    },
    onSuccess: (
      data,
      { viewID, recordID, parentPageID, documentID, onSuccess }
    ) => {
      if (viewID) {
        client.setQueryData(["sharedView", viewID, recordID], {
          exists: false,
        });
      }

      if (!viewID && parentPageID) {
        client.setQueryData(["sharedPage", parentPageID], {
          exists: false,
        });

        client.setQueryData(["page", parentPageID], (prev: any) => ({
          ...(prev || {}),
          Shared: false,
        }));
      }

      if (documentID) {
        client.setQueryData(["sharedDocument", documentID], (prev) => ({
          ...(prev || {}),
          exists: false,
        }));

        client.setQueryData(
          [tenantID, "record", "Documents", documentID],
          (prev: any) => ({
            ...(prev || {}),
            Shared: false,
          })
        );

        client.setQueriesData(
          {
            queryKey: [tenantID, "records", "Documents"],
          },
          (prev: any) => {
            if (!prev?.records?.length) return prev;

            const newDocs = prev.records.map((doc: any) => {
              if (doc._id === documentID) return { ...doc, Shared: false };
              return doc;
            });

            return { ...prev, records: newDocs };
          }
        );
      }

      onSuccess();

      const { updatedPage, updatedView } = data || {};

      // If a page was shared, the page's metadata was updated
      // OR If a view was unshared, that means the page's view metadata was also updated
      if (updatedPage) {
        client.setQueryData(["page", updatedPage._id], updatedPage);
      }
      // If a subview was unshared, that means the view's subView metadata was also updated
      if (updatedView) {
        client.setQueryData(["view", updatedView._id], updatedView);
      }
    },
    onError: (e, { onError }) => onError(getErrorMessage(e)),
  });

  return {
    unshareScreen: unshareScreen.mutate,
    unsharingScreen: unshareScreen.isPending,
  };
}
